import React, { Component, useContext, useEffect, useLayoutEffect, useState } from 'react';
import '../charts.scss'
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { ThemeContext } from '../../../context/theme-context';
import ErrorWithReload from '../ErrorWithReload';

const StackedBarChart = (props) => { // props: {data,hideLabel,valueXField,categoryYField,onReload}
    const [chartData, setChartData] = useState([]);
    const [theme, setTheme] = useState('dark'); // ['dark', 'light'
    const currentTheme = useContext(ThemeContext).theme;
    const [chartId] = useState(`chartdiv-${Math.random().toString(36).substr(2, 9)}`); // Generate unique ID for each chart instance
    
    useEffect(() => {
        // root.dispose();

        if (props.data != undefined && props.data.length > 0) {
            setChartData(props.data);
            // console.log('setChartDataProps', props); // Optional: remove if not needed
        }

    }, [props.data]);

    useEffect(() => {
        setTheme(currentTheme);
    }, [currentTheme]);

    useLayoutEffect(() => {
        // Check if the element exists first to avoid errors
        const chartElement = document.getElementById(chartId);
        if (!chartElement) {
            console.error(`Chart element with ID ${chartId} not found`);
            return () => {}; // Return empty cleanup function
        }
        
        // Create root element
        let root = am5.Root.new(chartId);
        root.numberFormatter.set("numberFormat", "#,###.00");

        root.setThemes([
            am5themes_Animated.new(root),
        ]);

        let chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                panY: false,
                panX: false,
                layout: root.verticalLayout,
                minTooltipDistance: 50,
            })
        );

        // Create Y-axis
        var yAxis = chart.yAxes.push(
            am5xy.CategoryAxis.new(root, {
                categoryField: "code",
                renderer: am5xy.AxisRendererY.new(root, {
                    inversed: true,
                    cellStartLocation: 0.1,
                    cellEndLocation: 0.9,
                    minGridDistance: 30,
                })
            })
        );

        yAxis.get("renderer").grid.template.setAll({
            strokeOpacity: 1,
            stroke: theme === 'dark' ? am5.color('#626262') : am5.color('#E4E4E4'),
            strokeDasharray: [4, 4],
        });

        yAxis.get("renderer").labels.template.setAll({
            oversizedBehavior: "wrap",
            maxWidth: props.hideLabel === false ? 0 : 250,
            textAlign: "center",
            fontSize: 12,
            fontFamily: 'Roboto-Medium',
            fill: theme === 'dark' ? am5.color('#fff') : am5.color('#231F20'),
        });

        if (chartData && chartData.length > 0) {
            yAxis.data.setAll(chartData);
        }

        let axisMinValue = -10; // Default min value if no data or single point
        let axisMaxValue = 10;  // Default max value if no data or single point

        if (chartData && chartData.length > 0 && props.valueXField) {
            const values = chartData
                .map(item => item[props.valueXField])
                .filter(value => typeof value === 'number' && !isNaN(value));

            if (values.length > 0) {
                const dataMin = Math.min(...values);
                const dataMax = Math.max(...values);
                
                const range = dataMax - dataMin;
                let padding;

                if (range === 0) {
                    // If all values are the same, use 5% of the absolute value as padding,
                    // or a default padding if the value is 0.
                    padding = dataMin === 0 ? 1 : Math.abs(dataMin * 0.05);
                } else {
                    padding = range * 0.09; 
                }
                
                axisMinValue = dataMin - padding;
                axisMaxValue = dataMax + padding;

                // Ensure min and max are not the same if padding was 0 (e.g. dataMin was 0 and range was 0)
                if (axisMinValue === axisMaxValue) {
                    axisMinValue -= 1;
                    axisMaxValue += 1;
                }
            }
        }

        // Create X-Axis
        var xAxis = chart.xAxes.push(
            am5xy.ValueAxis.new(root, {
                renderer: am5xy.AxisRendererX.new(root, {
                    strokeOpacity: 0.1,
                    minGridDistance: 40,
                }),
                max: axisMaxValue, 
                min: axisMinValue 
            })
        );

        xAxis.get("renderer").grid.template.setAll({
            strokeOpacity: 1,
            stroke: theme === 'dark' ? am5.color('#626262') : am5.color('#E4E4E4'),
            strokeDasharray: [4, 4]
        });

        xAxis.get("renderer").labels.template.setAll({
            oversizedBehavior: "wrap",
            maxWidth: 150,
            textAlign: "center",
            fontSize: 12,
            fontFamily: 'Roboto-Medium',
            fill: theme === 'dark' ? am5.color('#fff') : am5.color('#231F20'),
        });

        // Create series 2
        var tooltipHTML = createTooltipHTML(theme);

        let tooltipSeries = am5.Tooltip.new(root, {
            keepTargetHover: true,
            getFillFromSprite: true,
            getStrokeFromSprite: true,
            autoTextColor: true,
            getLabelFillFromSprite: true,
            fill: am5.color("#333"),
        });

        tooltipSeries.get("background").setAll({
            fill: am5.color("#333"),
            fillOpacity: 0,
            strokeOpacity: 0,
            getStrokeFromSprite: true,
        });
        tooltipSeries.label.set("fill", am5.color("#231f20"));
        
        let series = chart.series.push(
            am5xy.ColumnSeries.new(root, {
                xAxis: xAxis,
                yAxis: yAxis,
                valueXField: props.valueXField,
                categoryYField: props.categoryYField,
                sequencedInterpolation: true,
                clustered: true,
                stroke: theme === 'dark' ? am5.color('#35353e') : am5.color('#fff'),
                fill: theme === 'dark' ? am5.color('#35353e') : am5.color('#fff'),
                tooltip: tooltipSeries,
                tooltipHTML: tooltipHTML,
            })
        );

        series.columns.template.setAll({
            fillOpacity: 1,
            strokeWidth: 0,
            cornerRadiusTR: 5,
            cornerRadiusBR: 5,
        });

        series.columns.template.adapters.add("fill", function (fill, target) {
            var dataItem = target.dataItem;
            if (dataItem && dataItem.get("valueX") < 0) {
                target.set("tooltipY", am5.p100);
                series.columns.template.set("fillGradient", am5.LinearGradient.new(root, {
                    stops: [{
                        color: am5.color('#CB2D3E')
                    }, {
                        color: am5.color('#EF473A')
                    }],
                    rotation: 45
                }));
            }
            else {
                target.set("tooltipY", am5.p0);
                series.columns.template.set("fillGradient", am5.LinearGradient.new(root, {
                    stops: [{
                        color: am5.color('#418079')
                    }, {
                        color: am5.color('#2FBC99')
                    }],
                    rotation: 45
                }));
            }
        });

        if (chartData && chartData.length > 0) {
            series.data.setAll(chartData);
        }

        series.bullets.push(function () {
            return am5.Bullet.new(root, {
                locationX: 1,
                sprite: am5.Label.new(root, {
                    text: "% {valueX}",
                    fill: theme === 'dark' ? am5.color('#fff') : am5.color('#231F20'),
                    centerY: am5.p50,
                    centerX: am5.p100,
                    populateText: true,
                    fontSize: 14,
                })
            });
        });

        // Add cursor
        chart.set("cursor", am5xy.XYCursor.new(root, {}));

        // Proper cleanup function
        return () => {
            // Properly dispose of the root when component unmounts or when dependencies change
            if (root) {
                root.dispose();
            }
        };
    }, [chartData, theme, chartId, props.hideLabel, props.valueXField, props.categoryYField, props.selectedPeriod]);

    const createTooltipHTML = () => {
        // Temaya göre renkleri ayarlayın
        const greenColor = theme === 'dark' ? '#2FBC99' : '#2FBC99';
        const volumeColor = theme === 'dark' ? '#67b7dc' : '#67b7dc';
        const backgroundColor = theme === 'dark' ? '#121212' : '#FFFFFF';
        const textColor = theme === 'dark' ? '#fff' : '#231f20';
        const textGray = theme === 'dark' ? '#FFFFFF' : '#757575';

        return `
        <div style="font-size:16px; font-family:Roboto-Medium; padding: 5px; background-color: ${backgroundColor}; color:  ${textColor}; border-radius: 5px; min-width: auto; border:.5px solid #b5b5b5">
           
            <div style=" color:  ${greenColor};  font-size:16px; font-family:Roboto-Medium; display:block;"> 
                      {categoryY}      
            </div>


            <div style="display:block; font-size:16px; font-family:Roboto-Medium; color:  ${textColor};  text-align:right;"> 
               <span  style=" color:  ${greenColor};  font-size:16px; font-family:Roboto-Medium; padding-right:20px;"> ${props.selectedPeriod}: </span>  % {valueX.formatNumber('###.##')} 
            </div>
        </div>`;
    }

    // Check if we have valid data to render the chart
    const hasValidData = chartData && chartData.length > 0;
    const chartHeight = props.height ? props.height : "600px";
    
    return (
        <div className="stacked-bar-chart-container" style={{ width: "100%", height: chartHeight, position: 'relative' }}>
            {hasValidData ? (
                <div id={chartId} style={{ width: "100%", height: chartHeight }}></div>
            ) : (
                <ErrorWithReload 
                    onReload={() => props.onReload && props.onReload()}
                    height={chartHeight}
                    errorMessage={props.errorMessage || "No data available"}
                />
            )}
        </div>
    );
}

export default StackedBarChart;